import { Stack } from "@mui/system";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  setSelectedDataSet,
  setSelectedPeriod,
} from "../../../../../features/websiteTargetSlice";
import store from "../../../../../store";
import { useEffect } from "react";

interface Props {
  periods: any;
  selectedSharedUid: any;
  hasCreateWebsiteTargetPermission: any;
}

const WebsiteTargetTableHeading = ({
  periods,
  selectedSharedUid,
  hasCreateWebsiteTargetPermission,
}: Props) => {
  const selectedPeriod = useSelector(
    (state: any) => state.websiteTarget.selectedPeriod
  );
  const selectedDataSet = useSelector(
    (state: any) => state.websiteTarget.selectedDataSet
  );
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    store.dispatch(setSelectedPeriod(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    store.dispatch(setSelectedDataSet(searchParams.get("data-set")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodsDesc = periods?.sort((a:any, b:any) => {
    const [ma, ya] = a.split("/").map(Number);
    const [mb, yb] = b.split("/").map(Number);
    // Compare years first, then months
    return yb - ya || mb - ma;
  });

  return (
    <Paper
      sx={{
        boxShadow: "none",
        padding: "0px 16px 0px",
        borderEndEndRadius: "0px",
        borderBottomLeftRadius: "0px",
      }}
    >
      <Stack
        flexDirection={"row"}
        justifyContent={"start"}
        gap={2}
        sx={{
          padding: "20px 0px 16px",
        }}
      >
        <Autocomplete
          sx={{ width: "600px" }}
          options={periods ? periodsDesc : []}
          value={selectedPeriod}
          data-tracker="92"
          onChange={(_, newValue: any) => {
            store.dispatch(setSelectedPeriod(newValue));
            setSearchParams((prevParams) => {
              const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
              if (newValue) {
                updatedParams.search = newValue; // Set new value
              } else {
                delete updatedParams.search; // Remove from URL if cleared
              }
              updatedParams.page = "0";
              return updatedParams;
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Period"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ marginLeft: "8px" }} />
                  </InputAdornment>
                ),
              }}
              color="secondary"
            />
          )}
          size="small"
        />
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          sx={{ width: "100%" }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}
          >
            <FormLabel component="legend" sx={{ marginRight: 2 }}>
              Data Set:
            </FormLabel>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="data-set"
                name="data-set"
                value={selectedDataSet}
                onChange={(_, newValue: any) => {
                  store.dispatch(setSelectedDataSet(newValue));
                  setSearchParams((prevParams) => {
                    const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                    if (newValue) {
                      updatedParams["data-set"] = newValue; // Set new value
                    } else {
                      delete updatedParams["data-set"]; // Remove from URL if cleared
                    }
                    updatedParams.page = "0";
                    return updatedParams;
                  });
                }}
              >
                <FormControlLabel
                  value="Key Metrics"
                  control={<Radio color="secondary" />}
                  label="Key Metrics"
                />
                <FormControlLabel
                  value="Income"
                  control={<Radio color="secondary" />}
                  label="Income"
                />
                <FormControlLabel
                  value="Performance"
                  control={<Radio color="secondary" />}
                  label="Performance"
                />
                <FormControlLabel
                  value="All"
                  control={<Radio color="secondary" />}
                  label="All"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </Stack>
        <Stack
          flexDirection={"row"}
          justifyContent={"end"}
          sx={{ width: "100%" }}
        >
          {hasCreateWebsiteTargetPermission ? (
            <RouterLink
              to={`set_new_projection?${(() => {
                const params = new URLSearchParams(searchParams);
                params.set("page", "0");
                return params.toString();
              })()}`}
              state={{ selectedSharedUid: selectedSharedUid, periods: periods }}
            >
              <Button
                variant="contained"
                color="secondary"
                data-tracker="95"
                sx={{ whiteSpace: "nowrap" }}
              >
                <Stack flexDirection={"row"} gap={1}>
                  Set New Projection <AddIcon fontSize="small" />
                </Stack>
              </Button>
            </RouterLink>
          ) : (
            <Button
              variant="contained"
              disabled
              color="secondary"
              sx={{ whiteSpace: "nowrap" }}
            >
              <Stack flexDirection={"row"} gap={1}>
                Set New Projection <AddIcon fontSize="small" />
              </Stack>
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default WebsiteTargetTableHeading;
